import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row, SectionAlert, Surface, Txt } from '@vst/beam';
import { ErrorOutline } from '@vst/beam-icons/icons';

import { ContentProvider, Header } from '@buy-viasat/react';
import { selectAccessToken } from '@buy-viasat/redux/src/auth';
import {
  selectAddressLine2,
  selectFiberMultipleAddressesForPreviewProducts,
  selectHasFiberMduAddress,
  serviceabilityActions,
} from '@buy-viasat/redux/src/serviceability';
import { appActions, CustomerType, selectCustomerType, selectUrlParams } from '@buy-viasat/redux/src/app';

import '../services/i18n';
import i18n from '../services/i18n';
import styles from './serviceability.module.scss';

import FormAddress from '../components/FormAddress';
import { selectEnv } from '@buy-viasat/redux/src/env';
import { AemFeature, createAemUrl } from '@buy-viasat/utils';
import { navActions, Routes } from '@buy-viasat/redux/src/navigator';
import { planActions, selectHavePlansLoaded } from '@buy-viasat/redux/src/plan';

export function Serviceability() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['serviceability', 'beamCommon']);
  const accessToken = useSelector(selectAccessToken);
  const customerType = useSelector(selectCustomerType);
  const { offerId } = useSelector(selectUrlParams);
  const { aemUrl } = useSelector(selectEnv);

  const isBusinessUser = customerType === CustomerType.B;
  const closingOfferId = offerId ? getClosingOfferFeatureId() : undefined;

  const havePlansLoaded = useSelector(selectHavePlansLoaded);
  const addressLine2: string = useSelector(selectAddressLine2);

  const [isApartmentUnitRequirementMet, setIsApartmentUnitRequirementMet] = useState(false);
  const [isMduAlertClosedByUser, setIsMduAlertClosedByUser] = useState(false);
  const hasFiberMduAddress = useSelector(selectHasFiberMduAddress);
  const fiberMultipleAddressesForPreviewProducts = useSelector(selectFiberMultipleAddressesForPreviewProducts);
  const needsApartmentUnitField = hasFiberMduAddress || fiberMultipleAddressesForPreviewProducts;

  useEffect(() => {
    dispatch(planActions.setHavePlansLoaded(false));
  }, [havePlansLoaded, dispatch]);

  useEffect(() => {
    window.document.title = t('serviceability.meta.title');
    document.querySelector('meta[name="description"]')?.setAttribute('content', t('serviceability.meta.description'));
    dispatch(serviceabilityActions.onServiceabilityMount());
  }, [dispatch, t]);

  // User arrives on BV with an address in the query params
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (!queryParams.has('address')) {
      return;
    }

    const addressParam = queryParams.get('address');
    if (!addressParam) {
      return;
    }

    const addressObj = JSON.parse(addressParam);

    const address = {
      addressLines: addressObj.addressLines,
      municipality: addressObj.municipality,
      region: addressObj.region,
      postalCode: addressObj.postalCode,
      countryCode: addressObj.countryCode,
      houseNumber: addressObj.houseNumber,
    };

    setTimeout(() => {
      dispatch(serviceabilityActions.setServiceAddress(address));
      dispatch(
        serviceabilityActions.setCoordinates({
          latitude: addressObj.coordinates.latitude,
          longitude: addressObj.coordinates.longitude,
        }),
      );

      dispatch(
        serviceabilityActions.setScrubLocation({
          address,
          coordinates: {
            latitude: addressObj.coordinates.latitude,
            longitude: addressObj.coordinates.longitude,
          },
        }),
      );
      dispatch(appActions.resetPartyId());
      dispatch(navActions.routeUserTo(Routes.PLAN));
    });
  }, [dispatch]);

  useEffect(() => {
    if (accessToken) {
      dispatch(serviceabilityActions.setIsServiceabilityLoading(false));
    }
  }, [dispatch, accessToken]);

  useEffect(() => {
    if (needsApartmentUnitField && !addressLine2) {
      setIsApartmentUnitRequirementMet(false);
      setIsMduAlertClosedByUser(false);
    } else {
      setIsApartmentUnitRequirementMet(true);
    }
  }, [needsApartmentUnitField, addressLine2]);

  return (
    <div className={styles['root']}>
      <Surface>
        <Container fluidUntil={'xl'} className={styles['header']}>
          <Row edge2edge>
            <Col>
              <Header
                phoneNumber={t('common.header.phoneNumber.text')}
                linkUrl={t('common.header.logo.url')}
                linkPrefix={t('common.header.phoneNumber.prefix')}
              />
            </Col>
          </Row>
        </Container>
        <div className={styles['borderBottom']}></div>
      </Surface>
      <Surface variant="secondary" mx={{ lg: '48px' }} className={styles['main']}>
        <Container fluidUntil="md" className={styles['content']} pb={{ md: '40px' }}>
          <Row>
            <Col lg={{ span: 10, offset: 1 }}>
              <Surface
                variant={'secondary'}
                pt={closingOfferId ? '64px' : undefined}
                pb={closingOfferId ? '24px' : undefined}
              >
                {closingOfferId && (
                  <ContentProvider
                    id={closingOfferId}
                    src={createAemUrl(aemUrl, i18n.language, closingOfferId, '', customerType, false, '', '', offerId)}
                  />
                )}
              </Surface>
            </Col>
          </Row>
          <Row>
            <Col lg={{ span: 8, offset: 2 }}>
              <Surface className={styles['heading']} pt="28px" pb="32px" px="16px" mb="8px">
                <Txt variant="heading3" data-testid="serviceabilityHeadlineTitle" className={styles['heading-text']}>
                  {isBusinessUser ? t('serviceability.SMB.headline.title') : t('serviceability.headline.title')}
                </Txt>
              </Surface>
            </Col>
          </Row>
          {needsApartmentUnitField && !isApartmentUnitRequirementMet && !isMduAlertClosedByUser && (
            <Row>
              <Col lg={{ span: 8, offset: 2 }}>
                <SectionAlert
                  variant="error"
                  size="large"
                  mb="8px"
                  icon={ErrorOutline}
                  className={styles['mdu-error-alert']}
                  data-testid="serviceabilityMduError"
                  onClose={() => setIsMduAlertClosedByUser(true)}
                  contentSlot={
                    <Txt variant="bodySmallRegular" color="regular">
                      {t('common.mduError')}
                    </Txt>
                  }
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col lg={{ span: 8, offset: 2 }}>
              <Surface radius="16px" className={styles['card']}>
                <FormAddress
                  isApartmentUnitRequirementMet={isApartmentUnitRequirementMet}
                  onApartmentUnitProvided={() => setIsApartmentUnitRequirementMet(true)}
                />
              </Surface>
            </Col>
          </Row>
        </Container>
      </Surface>
    </div>
  );
}

export function getClosingOfferFeatureId() {
  return AemFeature.CLOSING_OFFERS;
}

export default Serviceability;
